import {
  ButtonGroup,
  ButtonV2,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  OdinIcon,
} from '@myosh/odin-components';
import { memo, useMemo } from 'react';
import type { BowtieDto as SaveBowtieDto } from '../../api/generated/bowtie-api';
import type { BowtieDto } from '../../api/generated/v4-api';
import { useAppPermissionsContext } from '../../context/app-permissions.context';
import { makeDataCy } from '../../helpers/util';
import useCreateBackendRecords from '../../hooks/use-create-backend-records-button-action';
import useDeleteDiagram from '../../hooks/use-delete-diagram-button-action';
import useSaveDiagram from '../../hooks/use-save-diagram-button-action';

interface CreateSplitButtonProps {
  bowtieDtoCreate?: BowtieDto;
  bowtieDtoSave?: SaveBowtieDto;
  mueLabel?: string;
  mueRecordId?: number;
}

const CreateSplitButton = ({ bowtieDtoCreate, bowtieDtoSave, mueLabel, mueRecordId }: CreateSplitButtonProps) => {
  const {
    loading: isSavingOrUpdating,
    label: saveOrUpdateButtonLabel,
    handleClick: handleSaveOrUpdateClick,
    diagramId,
  } = useSaveDiagram(bowtieDtoSave, mueLabel);

  // generate slug for fallback button selector
  const dataCyLabel = useMemo(() => makeDataCy(saveOrUpdateButtonLabel), [saveOrUpdateButtonLabel]);

  const { handleClick: handleCreateBackedRecordsClick, disabled: isCreateBackedRecordsDisabled } =
    useCreateBackendRecords(bowtieDtoCreate, mueLabel, mueRecordId);

  const { loading: isDeleting, handleClick: handleDeleteClick } = useDeleteDiagram(diagramId);

  const { canCreateBackendRecordsPermission } = useAppPermissionsContext();

  const canDelete = Boolean(diagramId);
  const canRenderSplitButton = canCreateBackendRecordsPermission || canDelete;

  const disabled = isSavingOrUpdating || isCreateBackedRecordsDisabled || isDeleting;

  if (!canRenderSplitButton) {
    return (
      <ButtonV2 size="lg" data-cy={`${dataCyLabel}-button`} onClick={handleSaveOrUpdateClick} disabled={disabled}>
        <OdinIcon icon="Save" data-icon="inline-start" />
        {saveOrUpdateButtonLabel}
      </ButtonV2>
    );
  }

  return (
    <ButtonGroup data-cy="split-button" disabled={disabled}>
      <ButtonV2 size="lg" onClick={handleSaveOrUpdateClick}>
        <OdinIcon icon="Save" data-icon="inline-start" />
        {saveOrUpdateButtonLabel}
      </ButtonV2>
      <DropdownMenu>
        <DropdownMenuTrigger render={<ButtonV2 size="icon-lg" aria-label="More save options" />}>
          <OdinIcon icon="ChevronDown" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canCreateBackendRecordsPermission && (
            <DropdownMenuItem onClick={handleCreateBackedRecordsClick}>
              <OdinIcon icon="SquarePlus" />
              Create Backend Records
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem onClick={handleDeleteClick}>
              <OdinIcon icon="Trash" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  );
};

export default memo(CreateSplitButton);
